import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Users as UsersIcon,
  Package,
  QrCode,
  ScrollText,
  Truck,
  HeartHandshake,
  Receipt,
  RotateCcw,
  CheckCircle2,
  RefreshCw,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { queryKeys } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/KpiCard";
import { TrendChart } from "@/components/TrendChart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_authed/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.kpis,
    queryFn: async () => {
      const res = await adminApi.getKpis()();
      return res.data;
    },
    // No timer. Each fetch is ~17 count() aggregations server-side plus the
    // admin-gate read, and a background tab nobody is looking at should cost
    // nothing. Wave-1 1.10 cut this from 60 s to 5 min; the remaining problem
    // was that a 5 min poll always missed the endpoint's 60 s memo, so every
    // one recomputed in full. Refetching on focus instead means a returning
    // admin gets current figures, and rapid re-checks land inside the memo.
    refetchInterval: false,
    refetchOnWindowFocus: true,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Platform-wide KPIs. Refreshes when you return to this tab.
        </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          {isFetching ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load KPIs: {(error as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Active users"
          value={fmt(data?.activeUsers, isLoading)}
          icon={<UsersIcon className="h-5 w-5" />}
        />
        <KpiCard
          label="Sign-ups today"
          value={fmt(data?.signupsToday, isLoading)}
          hint={data ? `${data.signupsThisWeek} this week` : undefined}
          icon={<UsersIcon className="h-5 w-5" />}
        />
        <KpiCard
          label="Items registered"
          value={fmt(data?.itemsRegistered, isLoading)}
          icon={<Package className="h-5 w-5" />}
        />
        <KpiCard
          label="Tags activated"
          value={fmt(data?.tagsActivated, isLoading)}
          icon={<QrCode className="h-5 w-5" />}
        />
        <KpiCard
          label="Claims submitted"
          value={fmt(data?.claimsSubmitted, isLoading)}
          icon={<ScrollText className="h-5 w-5" />}
        />
        <KpiCard
          label="Postal returns in flight"
          value={fmt(data?.postalReturnsInFlight, isLoading)}
          icon={<Truck className="h-5 w-5" />}
        />
        <KpiCard
          label="Charity donations"
          value={fmtPence(data?.charityDonationsTotalPence, isLoading)}
          icon={<HeartHandshake className="h-5 w-5" />}
        />
        <KpiCard
          label="Tag-order revenue"
          value={fmtPence(data?.feeRevenuePence, isLoading)}
          hint={
            data ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <span>{data.refundRatePct.toFixed(1)}% refunded</span>
                {data.revenueSource === "scan" ? (
                  <span
                    className="rounded-sm border border-amber-500 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-amber-600"
                    title="Counter backfill has not run — this figure is computed by scanning every paid order and charity on each poll."
                  >
                    scanned
                  </span>
                ) : null}
              </span>
            ) : undefined
          }
          icon={<Receipt className="h-5 w-5" />}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sign-ups — last 30 days</CardTitle>
        </CardHeader>
        <CardContent>
          {data ? (
            <TrendChart data={data.signupsByDay} />
          ) : (
            <div className="h-[200px] animate-pulse rounded-md bg-muted/50" />
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Items by status</CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownList items={data?.itemsByStatus} loading={isLoading} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-2">
                <RotateCcw className="h-4 w-4" /> Returns funnel
              </span>
            </CardTitle>
            <p className="text-xs text-muted-foreground">In flight now — where returns are sitting.</p>
          </CardHeader>
          <CardContent>
            <BreakdownList
              items={data?.returnsFunnel.map((r) => ({ status: r.state, count: r.count }))}
              loading={isLoading}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> Return outcomes
            </span>
          </CardTitle>
          {/* Kept out of the funnel deliberately: these are cumulative totals,
              so as bars beside the live stages they would grow without bound
              and hide the stalls the funnel exists to surface. */}
          <p className="text-xs text-muted-foreground">All time, cumulative — not funnel stages.</p>
        </CardHeader>
        <CardContent>
          <BreakdownList
            items={data?.returnOutcomes.map((r) => ({ status: r.state, count: r.count }))}
            loading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function BreakdownList({
  items,
  loading,
}: {
  items?: { status: string; count: number }[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-5 animate-pulse rounded bg-muted/50" />
        ))}
      </div>
    );
  }
  if (!items || items.length === 0) {
    return <div className="text-sm text-muted-foreground">No data.</div>;
  }
  return (
    <ul className="divide-y">
      {items.map((item) => (
        <li key={item.status} className="flex items-center justify-between py-2 text-sm">
          <span className="capitalize text-muted-foreground">{item.status.replace(/_/g, " ")}</span>
          <span className="font-medium">{item.count.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

function fmt(n: number | undefined, loading: boolean): string {
  if (loading) return "…";
  return n?.toLocaleString() ?? "—";
}

function fmtPence(n: number | undefined, loading: boolean): string {
  if (loading) return "…";
  if (n === undefined) return "—";
  return `£${(n / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

